import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * Las personas del ecosistema.
 *
 * Salen de Keycloak, que es donde viven de verdad. Aqui no hay una lista
 * propia: dos listas de personas acaban diciendo cosas distintas, y la que
 * manda para entrar es la de Keycloak.
 */

export interface PersonaDelEcosistema {
  id: string;
  usuario: string;
  correo: string | null;
  nombre: string;
  activa: boolean;
  roles: string[];
  /** Nombres de las aplicaciones que vera al entrar. */
  aplicaciones: string[];
  creadaEn: string | null;
}

export const personasKeys = {
  todas: ['personas'] as const,
};

export function usePersonas(enabled = true) {
  return useQuery<PersonaDelEcosistema[]>({
    queryKey: personasKeys.todas,
    queryFn: () => api.get<PersonaDelEcosistema[]>('/api/admin/users'),
    enabled,
  });
}

export function useCambiarContrasena() {
  return useMutation({
    mutationFn: ({
      id,
      contrasena,
      temporal,
    }: {
      id: string;
      contrasena: string;
      temporal: boolean;
    }) => api.put(`/api/admin/users/${id}/password`, { contrasena, temporal }),
  });
}

export function useCambiarEstado() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, activa }: { id: string; activa: boolean }) =>
      api.put(`/api/admin/users/${id}/estado`, { activa }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: personasKeys.todas }),
  });
}

/* ------------------------------------------- aplicaciones de cada persona */

export interface AplicacionDeLaPersona {
  id: string;
  nombre: string;
  slug: string;
  iconoUrl: string | null;
  estado: 'ACTIVE' | 'MAINTENANCE' | 'INACTIVE' | 'COMING_SOON';
  /** Si hoy la tiene concedida. */
  tiene: boolean;
}

export interface AccesosDePersona {
  revision: number;
  aplicaciones: AplicacionDeLaPersona[];
}

export const aplicacionesDePersonaKeys = {
  de: (id: string) => ['personas', id, 'aplicaciones'] as const,
};

export function useAplicacionesDePersona(id: string | null) {
  return useQuery<AccesosDePersona>({
    queryKey: aplicacionesDePersonaKeys.de(id ?? ''),
    queryFn: () => api.get<AccesosDePersona>(`/api/admin/users/${id}/aplicaciones`),
    enabled: id !== null,
  });
}

/** Guarda contra la revisión con la que se abrió el formulario. */
export function useFijarAplicaciones() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      aplicaciones,
      expectedRevision,
    }: {
      id: string;
      aplicaciones: string[];
      expectedRevision: number;
    }) =>
      api.put<AccesosDePersona>(`/api/admin/users/${id}/aplicaciones`, {
        aplicaciones,
        expectedRevision,
      }),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: aplicacionesDePersonaKeys.de(id) });
      // La lista de personas muestra las aplicaciones de cada una: si no se
      // refresca, la fila sigue diciendo lo de antes.
      void queryClient.invalidateQueries({ queryKey: personasKeys.todas });
    },
  });
}
